<script setup lang="ts">
import { reactive } from 'vue'
import { NButton, NInput } from 'naive-ui'
import { useAgentReposStore, type AgentRepoStatus } from '../stores/agent_repos'
import { useAsyncAction } from '../composables/useAsyncAction'

// Advanced/rare override (#949): a repo whose OWN devcontainer mounts its
// checkout somewhere other than the host `/workspaces/<slug>` path (e.g.
// `/app`, a common Rails devcontainer convention) needs this set, or every
// `devcontainer exec` against it fails to chdir — see
// Agent::DevcontainerCli#exec's container_workspace: cd-wrap.
const props = defineProps<{ repo: AgentRepoStatus }>()
const store = useAgentReposStore()

// Local draft, seeded once — mirrors the token/product-owner drafts'
// "edit locally, apply explicitly via Save" pattern rather than an
// instant-apply binding.
const draft = reactive({ containerWorkspace: props.repo.container_workspace_folder })

const { loading: saving, run: save } = useAsyncAction(
  () => store.update(props.repo.slug, { container_workspace_folder: draft.containerWorkspace }),
  {
    successMessage: () => `${props.repo.slug} container workspace saved`,
    failureLabel: 'Failed to update container workspace',
  },
)
</script>

<template>
  <label class="flex items-center gap-2 flex-wrap text-xs text-ink-muted">
    Container workspace override
    <span class="text-ink-faint font-normal">(advanced — only if this repo's devcontainer mounts elsewhere)</span>
    <NInput
      v-model:value="draft.containerWorkspace"
      size="small"
      placeholder="/workspaces/&lt;slug&gt; (default)"
      class="w-56"
      :data-testid="`agent-repo-container-workspace-input-${repo.slug}`"
    />
    <NButton
      size="small"
      :loading="saving"
      :data-testid="`agent-repo-container-workspace-save-${repo.slug}`"
      @click="save"
      >Save</NButton
    >
  </label>
</template>
